import React, { useState } from 'react';
import VideoModal from 'src/components/VideoModal';
import emailImage from 'src/images/email.svg';
import logo from 'src/images/logo-md.png';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  Icon,
  Label,
  Link,
  Row,
  Stripes,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useApp
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import { truckType } from 'straightline-utils/constants';
import { formatDate } from 'straightline-utils/time';

const initialValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  mc_number: '',
  dot_number: '',
  w9_document: '',
  insurance_document: ''
};

export default function Signup() {
  const app = useApp();
  const [success, setSuccess] = useState(false);

  const { result } = app.service('api/signup').useFind();

  if (success) {
    return (
      <Container fluid>
        <Row className="align-items-center justify-content-center vh-100">
          <Col xs="12" md="5" lg="4">
            <div className="px-3">
              <img
                src={emailImage}
                alt="Check Email"
                className="img-fluid mb-5 animated slideInDown"
              />
            </div>
            <div className="display-4 mb-3 animated slideInUp">
              Almost there!
            </div>
            <p className="text-muted lead mb-3 animated slideInUp">
              Check your email to validate your account. If you do not see an
              email, be sure to check your spam filter. Once validated, you will
              create your password and be ready to go.
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col
          xs="12"
          md="6"
          lg="5"
          xl="4"
          className="px-lg-5 border-lg-right d-flex align-items-center min-h-100vh"
        >
          <div className="w-100">
            <div className="text-center my-4">
              <img
                src={logo}
                alt="Straightline Logo"
                className="img-fluid"
                style={{ height: 60 }}
              />
            </div>
            <SignupForm handleSuccess={() => setSuccess(true)} />
          </div>
        </Col>
        <Col lg="7" xl="8" className="d-none d-lg-block px-0">
          <div
            className="d-flex align-items-center py-4"
            style={{
              overflow: 'hidden',
              position: 'relative',
              minHeight: '100vh'
            }}
          >
            <Stripes animation="fadeIn" />
            <Container fluid className="px-4">
              <Row className="d-flex justify-content-center">
                <Col xl="9">
                  <div className="text-white">
                    <h1 style={{ fontSize: '3.5rem' }} className="mb-4">
                      Freight to the Point
                    </h1>
                    <h3 style={{ fontSize: '1.5rem' }} className="mb-4">
                      Let the loads find you, right now.
                    </h3>
                    <p className="lead mb-4">
                      Get competitive rates and book a load in seconds without
                      leaving the app. After signing in, you can immediately
                      access the Straightline Dashboard and begin booking loads.
                      Straightline has no subscription fees or hidden costs.
                    </p>
                  </div>
                  <Card style={{ height: 442 }}>
                    <div className="table-responsive">
                      <Table className="table table-nowrap card-table table-hover">
                        <TableHead>
                          <TableRow>
                            <TableHeader className="px-3 px-xl-4">
                              Payout
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Pickup
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Delivery
                            </TableHeader>
                            <TableHeader className="px-3 px-xl-4">
                              Service
                            </TableHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody className="list">
                          {result?.data?.map((match) => {
                            return (
                              <TableRow
                                key={match.id}
                                onClick={() => window.open(match.url, '_blank')}
                                className="pointer"
                              >
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ width: '1%' }}
                                >
                                  <div className="h1 mb-0 text-primary">
                                    {formatMoney(
                                      match.rate.price.carrier_price,
                                      {
                                        precision: 0
                                      }
                                    )}
                                  </div>
                                </TableData>
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ width: '1%' }}
                                >
                                  <p className="mb-0">
                                    {match.pickup_address.city},{' '}
                                    {match.pickup_address.state}
                                  </p>
                                  <p className="mb-0">
                                    {formatDate(
                                      match.pickup_date,
                                      'LLL d, yyyy'
                                    )}
                                  </p>
                                </TableData>
                                <TableData
                                  className="px-3 px-xl-4"
                                  style={{ position: 'relative', width: '1%' }}
                                >
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: -12,
                                      top: 21
                                    }}
                                  >
                                    <Icon
                                      type="arrow-right text-success"
                                      style={{ fontSize: 25 }}
                                    />
                                  </div>
                                  <p className="mb-0">
                                    {match.delivery_address.city},{' '}
                                    {match.delivery_address.state}
                                  </p>
                                  <p className="mb-0">
                                    {formatDate(match.due_date, 'LLL d, yyyy')}
                                  </p>
                                </TableData>
                                <TableData>
                                  <div>{match.rate.service_level.name}</div>
                                  <div className="d-flex align-items-center">
                                    <Icon
                                      type="truck"
                                      className="text-secondary"
                                    />
                                    <span className="ml-1">
                                      {truckType(match.truck_type).label}
                                    </span>
                                  </div>
                                </TableData>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </Card>
                  <div>
                    <VideoModal className="mr-3" />
                    <a
                      href="https://www.bookstraightline.com/carriers"
                      className="btn btn-white"
                    >
                      Learn More
                    </a>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </Col>
      </Row>

      {/* Preload the image so it does not flash when loaded later */}
      <img src={emailImage} alt="Check Email" className="d-none" />
    </Container>
  );
}

// There is not really a "code" sent from the
// server indicating which validation rule it
// failed. We just get "This ${label} is not available"
// when something fails a .unique() validation
const hasUniqueError = (errors) => {
  return Object.values(errors).find((message) =>
    message?.includes('is not available')
  );
};

const fileToBase64 = async (file) => {
  if (file.type.startsWith('image/')) {
    const imageBitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = imageBitmap.width;
    canvas.height = imageBitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imageBitmap, 0, 0);

    const compressed = await new Promise((resolve) => {
      return canvas.toBlob(resolve, file.type, 0.8);
    });

    file = new File([compressed], file.name, {
      type: compressed.type,
      lastModified: Date.now()
    });
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (e) => reject(e);
  });
};

export function SignupForm({ handleSuccess, matchId }) {
  const [tab, setTab] = useState('highway');
  const app = useApp();
  const [uniqueError, setUniqueError] = useState(null);
  const { schema, useCreate } = app.service('api/signup');

  const createSignup = useCreate().create;

  const handleSubmit = async (data, formik) => {
    formik.setSubmitting(true);
    data = { ...data };
    const w9File = window.document.getElementById('w9_document').files[0];
    const insuranceFile =
      window.document.getElementById('insurance_document').files[0];

    data.w9_document = await fileToBase64(w9File);

    if (insuranceFile) {
      data.insurance_document = await fileToBase64(insuranceFile);
    }

    await createSignup(data)
      .then((data) => {
        formik.setSubmitting(false);
        handleSuccess(data);
      })
      .catch((error) => {
        formik.setSubmitting(false);
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        if (error.errors && hasUniqueError(error.errors)) {
          setUniqueError(error.errors);
        } else {
          toast.errors(error);
        }
      });
  };

  let href = `${process.env.REACT_APP_HIGHWAY_AUTH_URL}`;

  if (matchId) {
    href += `&state=${matchId}`;
  }

  if (tab === 'highway') {
    return (
      <div>
        <div className="mb-3">
          Straightline uses Highway to onboard and verify carriers. Click the
          button below to sign in. You can then return to Straightline to access
          the dashboard and book loads.
        </div>
        <div className="mb-4">
          If you are having trouble signing in, please contact{' '}
          <a href="mailto:support@straightline.com">
            {process.env.REACT_APP_SUPPORT_EMAIL}
          </a>
        </div>
        <Button
          tag="a"
          href={href}
          color="warning"
          size="lg"
          block
          // disabled={isSubmitting}
          className="mb-2"
          style={{
            color: 'white',
            backgroundColor: '#F8C617',
            borderColor: '#F8C617'
          }}
        >
          <svg
            className="mr-2"
            width="22"
            height="22"
            viewBox="0 0 35 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.7139 21.802H34.4453L25.7554 0.199219H19.8897L20.8673 7.90674H14.024L15.1103 0.199219H9.24458L0.554688 21.802H12.286L13.2636 14.0945H21.7363L22.7139 21.802Z"
              fill="white"
            />
          </svg>
          <span>Sign up with Highway</span>
        </Button>
        <Button
          size="lg"
          color="primary"
          onClick={() => setTab('straightline')}
          block
        >
          Sign up with Email
        </Button>
        <div className="mt-2">
          <p className="text-muted small">
            By signing up, you expressly agree to the Straightline&nbsp;
            <Link to="/terms-of-service" target="_blank">
              Terms of Service
            </Link>
            . <br /> Already have an account?&nbsp;
            <Link to="/login">Sign in</Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
        render={({ isSubmitting }) => {
          return (
            <Form>
              <Row>
                <Col sm="6">
                  <FormGroup>
                    <Label>First Name*</Label>
                    <FormInput
                      name="first_name"
                      placeholder="Enter first name"
                      disabled={isSubmitting}
                    />
                    <FormFeedback name="first_name" />
                  </FormGroup>
                </Col>
                <Col sm="6">
                  <FormGroup>
                    <Label>Last Name*</Label>
                    <FormInput
                      name="last_name"
                      placeholder="Enter last name"
                      disabled={isSubmitting}
                    />
                    <FormFeedback name="last_name" />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Email Address*</Label>
                <FormInput
                  type="email"
                  name="email"
                  placeholder="name@address.com"
                  disabled={isSubmitting}
                />
                <FormFeedback name="email" />
              </FormGroup>
              <FormGroup>
                <Label>Phone*</Label>
                <FormInput type="phone" name="phone" disabled={isSubmitting} />
                <FormFeedback name="phone" />
              </FormGroup>
              <Row>
                <Col sm="6">
                  <FormGroup>
                    <Label>MC Number*</Label>
                    <FormInput
                      name="mc_number"
                      placeholder="Enter MC number"
                      maxLength="8"
                      disabled={isSubmitting}
                    />
                    <FormFeedback name="mc_number" />
                  </FormGroup>
                </Col>
                <Col sm="6">
                  <FormGroup>
                    <Label>DOT Number*</Label>
                    <FormInput
                      name="dot_number"
                      placeholder="Enter DOT number"
                      maxLength="8"
                      disabled={isSubmitting}
                    />
                    <FormFeedback name="dot_number" />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>
                  Upload Carrier Packet or W9 and Insurance Document
                </Label>
                <FormInput
                  type="file"
                  name="w9_document"
                  accept=".jpg, .png, .pdf, .jpeg"
                  disabled={isSubmitting}
                  id="w9_document"
                />
                <FormInput
                  className="mt-2"
                  type="file"
                  name="insurance_document"
                  accept=".jpg, .png, .pdf, .jpeg"
                  disabled={isSubmitting}
                  id="insurance_document"
                />
                <FormFeedback detached name="w9_document" />
                <FormFeedback detached name="insurance_document" />
              </FormGroup>
              {uniqueError && (
                <Alert color="danger">
                  {uniqueError.email && (
                    <div>
                      It appears you are trying to sign up with an email that
                      already exists. Go to the{' '}
                      <Link to="/login" className="alert-link">
                        Login Page
                      </Link>{' '}
                      or{' '}
                      <Link to="/forgot-password" className="alert-link">
                        Reset Your Password
                      </Link>{' '}
                      to continue.
                      {(uniqueError.mc_number || uniqueError.dot_number) && (
                        <hr className="border-light my-3" />
                      )}
                    </div>
                  )}
                  {(uniqueError.mc_number || uniqueError.dot_number) && (
                    <div>
                      The MC or DOT number you provided belongs to an account
                      that is already registered. Contact the account owner for
                      an invitation. You can also{' '}
                      <Link to="/login" className="alert-link">
                        Contact Straightline
                      </Link>{' '}
                      if you believe this is a mistake.
                    </div>
                  )}
                </Alert>
              )}
              <Button
                type="submit"
                color="primary"
                size="lg"
                block
                className={`mb-3 ${isSubmitting && 'is-loading'}`}
                disabled={isSubmitting}
              >
                Sign up
              </Button>
              <div className="">
                <p className="text-muted small">
                  By signing up, you expressly agree to the Straightline&nbsp;
                  <Link to="/terms-of-service" target="_blank">
                    Terms of Service
                  </Link>
                  . Already have an account?&nbsp;
                  <Link to="/login">Sign in</Link>. If you are having trouble
                  signing up, please contact{' '}
                  <a href="mailto:support@straightline.com">
                    {process.env.REACT_APP_SUPPORT_EMAIL}
                  </a>
                </p>
              </div>
            </Form>
          );
        }}
      />
    </div>
  );
}
